import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Briefcase, Code2, Sparkles } from 'lucide-react';
import Section from '../ui/Section';
import SectionHeader from '../ui/SectionHeader';

const About: React.FC = () => {
  const { t } = useTranslation();
  
  const experiences = [
    {
      icon: Sparkles,
      key: 'roqt',
      gradient: 'from-primary to-secondary',
    },
    {
      icon: Code2,
      key: 'rpa',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Briefcase,
      key: 'telcom',
      gradient: 'from-orange-500 to-yellow-500',
    },
  ];
  
  return (
    <Section id="about">
      <SectionHeader
        title={t('about.title')}
        subtitle={t('about.subtitle')}
      />
      
      <div className="max-w-5xl mx-auto">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {t('about.intro')}
          </p>
        </motion.div>
        
        {/* Experience Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {experiences.map((exp, index) => {
            const Icon = exp.icon;
            return (
              <motion.div
                key={exp.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-white/10 border border-white/10 rounded-xl p-6 hover:border-primary/50 hover:bg-white/[0.12] transition-all duration-300 group overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {/* Icon */}
                <div className={`relative w-14 h-14 rounded-lg bg-gradient-to-br ${exp.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                
                {/* Company & Role */}
                <h3 className="text-xl font-bold text-white mb-2">
                  {t(`about.experiences.${exp.key}.company`)}
                </h3>
                <p className="text-primary font-medium mb-1">
                  {t(`about.experiences.${exp.key}.role`)}
                </p>
                <p className="text-sm text-gray-400 mb-4">
                  {t(`about.experiences.${exp.key}.period`)}
                </p>
                
                {/* Description */}
                <p className="text-gray-300 text-sm leading-relaxed">
                  {t(`about.experiences.${exp.key}.description`)}
                </p>
              </motion.div>
            );
          })}
        </div>
        
        {/* Skills Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8"
        >
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            {t('about.expertise.title')}
          </h3>
          <p className="text-gray-300 text-center leading-relaxed max-w-3xl mx-auto">
            {t('about.expertise.description')}
          </p>
        </motion.div>
      </div>
    </Section>
  );
};

export default About;
